import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Session } from '@/types/session';
import { sessionStorage } from '@services/storage/session-storage';
import { SessionList } from '@components/session/SessionList';
import { Loading } from '@components/common/Loading';
import { useSessionNavigationStore } from '@store/session-navigation-store';
import { logger } from '@utils/logger';

export function ActiveSessionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeSessionId } = useSessionNavigationStore();
  const [activeSessions, setActiveSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActiveSessions();
  }, []);

  const loadActiveSessions = async () => {
    try {
      setLoading(true);
      const allSessions = await sessionStorage.getAll();
      const active = allSessions.filter(
        (s) => s.status === 'in_progress' || s.status === 'paused'
      );

      if (active.length === 0) {
        // No active sessions — redirect to new session form
        navigate('/session/new', { replace: true });
        return;
      }

      setActiveSessions(active);
    } catch (error) {
      logger.error('Failed to load active sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading message={t('activeSessions.loadingSessions')} />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('activeSessions.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('activeSessions.subtitle')}
        </Typography>
      </Box>

      <SessionList sessions={activeSessions} activeSessionId={activeSessionId} />
    </Container>
  );
}
